import React from "react";
import Geocoding, { GeocodeService, GeocodeFeature } from "@mapbox/mapbox-sdk/services/geocoding";
import mapboxParser, { PlaceResult } from "src/helpers/mapboxParser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { Input, InputGroup, InputGroupText } from "reactstrap";

export interface PlaceSelectedCb {
  result?: PlaceResult;
  lat?: number;
  lon?: number;
  clear?: boolean;
  formEnter?: boolean;
}
export interface PlaceSelectCbResult {
  accepted: boolean;
}
// tslint:disable-next-line:no-empty-interface
interface AutocompleteProps {
  cb: (place: PlaceSelectedCb) => Promise<PlaceSelectCbResult>;
}
interface AutocompleteState {
  userInput: string;
  userSearch: string;
  focused: boolean;
  loading: boolean;
  suggestions: AcResponse[];
  arrowKeyIndex: number;
  hint: string;
}
interface AcResponse {
  desc: string;
  lat: number;
  lon: number;
  selected: boolean;
  mbParsed: PlaceResult;
}

export default class AutocompleteComponent extends React.Component<AutocompleteProps, AutocompleteState> {
  private node = React.createRef<HTMLDivElement>();

  private mapboxClient: GeocodeService;

  constructor(props: AutocompleteProps) {
    super(props);
    this.state = {
      userInput: "",
      userSearch: "",
      focused: false,
      loading: false,
      suggestions: [],
      hint: null,
      arrowKeyIndex: -1,
    };

    this.getAutoComplete = this.getAutoComplete.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.suggestionSelected = this.suggestionSelected.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.clear = this.clear.bind(this);
  }

  public getAutoComplete(event: any) {
    this.setState({
      userInput: event.target.value,
    });
    event.persist();
    if (event.target.value.length < 3) {
      this.setState({
        hint: "Type a few more characters..",
        suggestions: [],
      });
      if (!event.target.value) {
        this.setState({
          suggestions: [],
        });
      }
      return;
    }
    this.setState({
      hint: "Searching..",
      suggestions: [],
    });

    this.debounce(() => {
      this.setState({
        hint: null,
      });
      if (!event.target.value) {
        return;
      }
      this.setState({
        loading: true,
        userSearch: event.target.value,
      });
      const query = event.target.value;
      this.mapboxClient
        .forwardGeocode({
          query,
          limit: 5,
          types: ["address", "place"],
          mode: "mapbox.places",
          language: ["en"],
        })
        .send()
        .then((response) => {
          this.setState({
            loading: false,
            suggestions: response.body.features.map((feature: GeocodeFeature) => {
              const parsed = mapboxParser.parse(feature);
              console.log(parsed);
              return {
                desc: parsed.name,
                lat: parsed.lat,
                lon: parsed.lon,
                selected: false,
                mbParsed: parsed,
              } as AcResponse;
            }),
          });
        })
        .catch((error) => console.error(error, "unable to autocomplete"));
    }, 1000)();
  }

  public componentDidMount() {
    // we need to instantiate mapbox as follows bc of server side rendering
    if (typeof window !== "undefined") {
      this.mapboxClient = new (Geocoding as any)({ accessToken: process.env.MAPBOX }) as GeocodeService;
    }
    document.addEventListener("click", this.handleOutsideClick, false);
    document.addEventListener("keydown", this.handleKeyDown, false);
    document.addEventListener("keyup", this.handleKeyUp, false);
  }

  public componentWillUnmount() {
    document.removeEventListener("click", this.handleOutsideClick, false);
    document.removeEventListener("keydown", this.handleKeyDown, false);
    document.removeEventListener("keyup", this.handleKeyUp, false);
  }

  public onFocus() {
    if (this.state.focused) {
      return;
    }
    this.setState(() => ({
      focused: true,
      loading: false,
    }));
  }

  public handleOutsideClick(e: any) {
    // ignore clicks on the component itself
    const node = this.node.current;
    if (node) {
      if (node.contains(e.target)) {
        return;
      }
      this.setState({
        focused: false,
        arrowKeyIndex: -1,
      });
    }
  }

  public handleKeyUp(e: any) {
    // handles when the user clears the input
    if (e.target.value && e.target.value.length === 0) {
      this.props.cb(null);
      this.setState({
        hint: null,
      });
    }
  }

  // handles user operating form w/ keyboard
  public handleKeyDown(e: any) {
    const setActiveByIndex = (sugs: AcResponse[], index: number) => {
      sugs.forEach((v, i) => {
        v.selected = i === index;
      });
      return sugs;
    };
    // allow user to highlight the results
    if (this.state.suggestions && this.state.suggestions.length) {
      if (e.keyCode === 38) {
        // up arrow
        const keyIndex = this.state.arrowKeyIndex > 0 ? this.state.arrowKeyIndex - 1 : 0;
        this.setState({
          arrowKeyIndex: keyIndex,
          suggestions: setActiveByIndex(this.state.suggestions, keyIndex),
          userInput: this.state.suggestions[keyIndex].desc,
        });
      } else if (e.keyCode === 40) {
        // down arrow
        const keyIndex =
          this.state.arrowKeyIndex < this.state.suggestions.length - 1
            ? this.state.arrowKeyIndex + 1
            : this.state.arrowKeyIndex;
        this.setState({
          arrowKeyIndex: keyIndex,
          suggestions: setActiveByIndex(this.state.suggestions, keyIndex),
          userInput: this.state.suggestions[keyIndex].desc,
        });
      } else if (e.keyCode === 13) {
        // enter key
        if (this.state.arrowKeyIndex >= 0) {
          this.suggestionSelected(this.state.suggestions[this.state.arrowKeyIndex], true);
        }
      }
    }
  }

  public suggestionSelected(s: AcResponse, formEnter = false): void {
    this.props
      .cb({
        lat: s.lat,
        lon: s.lon,
        name: s.desc,
        formEnter,
        result: s.mbParsed,
      } as PlaceSelectedCb)
      .then((r) => {
        if (r.accepted) {
          this.setState({
            focused: false,
            userInput: "",
            suggestions: [],
          });
        }
      });
  }

  public clear() {
    this.props.cb({
      clear: true,
    });
    this.setState({
      focused: false,
      userInput: "",
      suggestions: [],
    });
  }

  // tslint:disable:max-line-length
  public render() {
    return (
      <div ref={this.node} onClick={this.onFocus} className="autocomplete">
        <InputGroup className="input-group-alternative">
          <Input
            autoFocus={false}
            value={this.state.userInput}
            type="text"
            placeholder="Add a location"
            onChange={this.getAutoComplete}
          />

          <InputGroupText>
            {this.state.userInput && this.state.focused && <FontAwesomeIcon icon={faTimes} onClick={this.clear} />}
          </InputGroupText>
        </InputGroup>
        {this.state.focused && (this.state.hint || this.state.suggestions.length > 0) && (
          <div className="results">
            {this.state.hint && <div>{this.state.hint}</div>}
            {this.state.suggestions.map((suggestion: AcResponse) => {
              return (
                <div
                  className={`cursor-pointer ${suggestion.selected ? "active" : ""}`}
                  onClick={() => this.suggestionSelected(suggestion)}
                  key={suggestion.desc}
                >
                  {suggestion.desc}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  private debounce(callback: () => void, wait: number, immediate = false) {
    let timeout: any = null;
    return () => {
      const callNow = immediate && !timeout;
      const next = () => callback.apply(this);
      clearTimeout(timeout);
      timeout = setTimeout(next, wait);
      if (callNow) {
        next();
      }
    };
  }
}
